import { distinctUntilChanged } from "rxjs/operators";
import {
  AfterViewChecked,
  Component,
  OnInit,
  inject,
  input,
  output,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { OnDestroy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { Subscription } from "rxjs";
import { QuoteRepository } from "../quote.repository";
import { Quote } from "../quote.type";
import { QuoteEmail } from "../models/email.model";
import { quoteStatus } from "../quote-form/quote-status.const";
import { NotifyService } from "../../notify/notify.service";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { ClientContactPersonRepository } from "../../client/client-contact-person-list/client-contact-person.repository";
import { validateEmailArrayFormControl } from "../../shared/email/email-array-validator";
import { SelectItem } from "primeng/api";
import {
  placeholder,
  placeholderLabel,
} from "../../ui/wysiwyg/config/placeholder.const";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { WysiwygComponent } from "app/ui/wysiwyg/wysiwyg.component";
import { ButtonDirective } from "primeng/button";
import { Message } from "primeng/message";
import { RadioButton } from "primeng/radiobutton";
import { Select } from "primeng/select";
import { AutoComplete } from "primeng/autocomplete";
import { Chip } from "primeng/chip";
import { UploadComponent } from "../../ui/upload/upload.component";
import { SafeHtmlPipe } from "../../shared/html/safe-html.pipe";

@Component({
  selector: "app-quote-mail-editor",
  templateUrl: "./quote-mail-editor.component.html",
  styleUrls: ["./quote-mail-editor.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    Message,
    RadioButton,
    FormsModule,
    Select,
    ReactiveFormsModule,
    AutoComplete,
    Chip,
    WysiwygComponent,
    UploadComponent,
    SafeHtmlPipe,
  ],
})
export class QuoteMailEditorComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  private formBuilder = inject(UntypedFormBuilder);
  private quoteRepository = inject(QuoteRepository);
  private notifyService = inject(NotifyService);
  private clientContactPersonRepository = inject(ClientContactPersonRepository);

  public readonly quote = input<Quote>(undefined);
  public readonly emailChange = output<QuoteEmail>();
  public readonly validityChange = output<boolean>();

  readonly subjectWysiwyg = viewChild<WysiwygComponent>("subjectWysiwyg");
  readonly messageWysiwyg = viewChild<WysiwygComponent>("messageWysiwyg");

  public defaultSubject =
    "[[my_company_name]] quote [[quote_number]] for [[client_name]] [[validity_dates]]";
  public defaultMessage =
    "[[client_greetings]]<br/><br/>[[quote_summary]]<br/><br/>[[my_signature]]";
  public quoteStatus = quoteStatus;
  public readonly recipientFields = [
    { name: "to", label: "To:" },
    { name: "cc", label: "CC:" },
    { name: "bcc", label: "BCC:" },
  ];
  public mailForm: UntypedFormGroup;
  public emailPreview: QuoteEmail = null;
  public placeholderValues: Record<string, string> | null = null;
  public previewLoading = false;
  public mailboxLoading = false;
  public mailSubjects: SelectItem[] = [];
  public isMailSubjectSelected = undefined;
  public results: string[];
  private subscription = new Subscription();
  private contactPersons = [];
  private selectedMessageId: string | null = null;
  private selectedReceivedAt: string | null = null;
  private replySourceChanged = false;
  private separatorKeys = [" ", ";", ",", "Tab"];
  private replaceKeys = [" ", ";", ","];
  // Mirrors the parent's own starting assumption, so a valid form emits once and a
  // pristine one emits nothing.
  private emittedValidity = false;

  ngOnInit() {
    this.refreshPreview();
    this.fetchMailSubjects();
    this.fetchContactPersons();
    this.createForm();
    this.observeFormChanges();
  }

  ngAfterViewChecked() {
    this.emitValidity();
  }

  // The parent binds this onto the Send button's disabled state, so emitting from
  // inside the check that spotted the change would rewrite a binding it has already
  // been checked for. Publish real changes only, once that pass has finished.
  private emitValidity(): void {
    const valid = !!this.mailForm?.valid;
    if (valid === this.emittedValidity) {
      return;
    }

    this.emittedValidity = valid;
    queueMicrotask(() => this.validityChange.emit(valid));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public search(event) {
    this.results = [
      event.query,
      ...this.contactPersons,
      "[[" + placeholder.clientEmails + "]]",
      "[[" + placeholder.myEmail + "]]",
      "[[" + placeholder.otherSalesReps + "]]",
    ];
  }

  public isValid(): boolean {
    return !!this.mailForm.valid;
  }

  public onMailSubjectChange(subject): void {
    if (subject instanceof QuoteEmail) {
      this.changeMailSubject(subject);
    } else if (!subject) {
      this.clearMailSubject();
    }
  }

  public onKeyUpField(event, targetField: string = "to"): void {
    if (this.separatorKeys.indexOf(event.key) !== -1) {
      let value = event.currentTarget.value;
      for (let i = 0; i < this.replaceKeys.length; i++) {
        value = value.replace(this.replaceKeys[i], "");
      }
      if (this.isValidEmail(value)) {
        event.currentTarget.value = "";

        const mails = Array.isArray(this.mailForm.controls[targetField].value)
          ? this.mailForm.controls[targetField].value
          : [];
        mails.push(value);
        this.mailForm.patchValue({
          targetField: mails,
        });
        this.mailForm.controls[targetField].updateValueAndValidity();
      }
    }
  }

  public onBlurField(event, targetField: string = "to"): void {
    const value = event.currentTarget.value;
    if (this.isValidEmail(value)) {
      event.currentTarget.value = "";
      const mails = Array.isArray(this.mailForm.controls[targetField].value)
        ? this.mailForm.controls[targetField].value
        : [];
      mails.push(value);
      this.mailForm.patchValue({
        targetField: mails,
      });
      this.mailForm.controls[targetField].updateValueAndValidity();
    }
  }

  public isValidEmail(email): boolean {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return re.test(String(email).toLowerCase());
  }

  public isMailSubjectDisabled(): boolean {
    return this.mailboxLoading || !this.mailSubjects.length;
  }

  // Drafts render the resolved values inside the editors, everything else renders the read only
  // preview - either way this is what the parent calls when the quote changed under us.
  public refreshPreview() {
    if (this.quote().getStatus() === quoteStatus.draft) {
      this.fetchPlaceholders();

      return;
    }

    this.fetchEmailPreview();
  }

  private fetchEmailPreview() {
    this.previewLoading = true;
    this.subscription.add(
      this.quoteRepository.getEmailPreview(this.quote().getId()).subscribe({
        next: (preview) => {
          this.emailPreview = preview;
          this.previewLoading = false;
        },
        error: (err) => {
          this.notifyService.toast(
            TOAST.ERROR,
            "Preview fetch error",
            <string>new BackendErrorPipe().transform(err),
          );
          this.previewLoading = false;
        },
      }),
    );
  }

  // Editing the email leaves the resolved values untouched - except when the reply source changed:
  // the backend fetches and persists the quoted body on that same PATCH, and it is a placeholder value.
  public refreshAfterQuoteUpdate(
    emailEdit: boolean,
    clientsChanged = false,
  ): void {
    // The backend resets the email when the client changes - the thread belonged to the previous
    // client - so whatever the form still holds is stale.
    if (clientsChanged) {
      this.syncFromQuoteEmail();
    }

    if (emailEdit && !this.replySourceChanged) {
      return;
    }

    this.replySourceChanged = false;
    this.refreshPreview();
  }

  private syncFromQuoteEmail(): void {
    const email = this.quote().getEmail();
    if (!email) {
      return;
    }

    this.selectedMessageId = null;
    this.selectedReceivedAt = null;
    this.isMailSubjectSelected = false;
    this.replySourceChanged = false;

    // The values come from the backend, so re-emitting them would only PATCH what it already stored.
    this.mailForm.patchValue(
      {
        to: email.getTo() ? email.getTo().split(",") : [],
        cc: email.getCc() ? email.getCc().split(",") : [],
        bcc: email.getBcc() ? email.getBcc().split(",") : [],
        subject: email.getSubject(),
        message: email.getMessage(),
        attachments: email.getAttachments(),
      },
      { emitEvent: false },
    );
    this.subjectWysiwyg().updateValue(email.getSubject(), true);
    this.messageWysiwyg().updateValue(email.getMessage(), true);

    this.contactPersons = [];
    this.fetchContactPersons();
    this.fetchMailSubjects();
  }

  private fetchPlaceholders() {
    this.subscription.add(
      this.quoteRepository
        .getEmailPlaceholders(this.quote().getId())
        .subscribe({
          next: (values) => {
            this.placeholderValues = values;
          },
          error: () => {
            // Without values the editors keep rendering the placeholder names, so the form stays usable.
            this.placeholderValues = {};
            this.notifyService.toast(
              TOAST.ERROR,
              "Preview fetch error",
              "Email placeholders have not been fetched",
            );
          },
        }),
    );
  }

  public resolveRecipient(recipient: string): string {
    const id = this.recipientPlaceholderId(recipient);
    if (!id) {
      return recipient;
    }

    return this.placeholderValues?.[id] || placeholderLabel[id] || recipient;
  }

  // The suggestions offer a placeholder, so they name it - what it resolves to belongs on the
  // chip the user gets after picking it.
  public nameRecipient(recipient: string): string {
    const id = this.recipientPlaceholderId(recipient);

    return (id && placeholderLabel[id]) || recipient;
  }

  private recipientPlaceholderId(recipient: string): string | undefined {
    return /^\[\[(.+?)]]$/.exec(recipient)?.[1];
  }

  // A reply carries the subject and body of the message it answers, so there is no default to
  // go back to while one is selected.
  public isRestoreDefaultSubjectVisible(): boolean {
    return !this.isMailSubjectSelected && this.isDefaultSubjectChanged();
  }

  public isRestoreDefaultMessageVisible(): boolean {
    return !this.isMailSubjectSelected && this.isDefaultMessageChanged();
  }

  public restoreDefaultSubject(): void {
    this.mailForm.patchValue({ subject: this.defaultSubject });
    this.subjectWysiwyg().updateValue(this.defaultSubject, true);
  }

  public restoreDefaultMessage(): void {
    this.mailForm.patchValue({ message: this.defaultMessage });
    this.messageWysiwyg().updateValue(this.defaultMessage, true);
  }

  private isDefaultSubjectChanged(): boolean {
    return (
      this.normalizeTemplate(this.mailForm.controls.subject.value) !==
      this.normalizeTemplate(this.defaultSubject)
    );
  }

  public getFileName(url): string {
    return url.split("/").pop();
  }

  private isDefaultMessageChanged(): boolean {
    return (
      this.normalizeTemplate(this.mailForm.controls.message.value) !==
      this.normalizeTemplate(this.defaultMessage)
    );
  }

  // The editor re-wraps content into <p> blocks as soon as it renders it, so the stored value and
  // the template are only comparable once both are reduced to the same shape. Inline formatting is
  // deliberately kept - bolding the greeting is still a change away from the default.
  private normalizeTemplate(value: string): string {
    return (value ?? "")
      .replace(/<\/?p[^>]*>/gi, "<br>") // Paragraph boundaries read as line breaks
      .replace(/<br\s*\/?>/gi, "<br>")
      .replace(/\s/g, "") // Spaces and new lines
      .replace(/[\u200B-\u200D\uFEFF]/g, "") // Zero-width space
      .replace(/(?:<br>)+/g, "<br>") // Blank-line runs
      .replace(/^<br>|<br>$/g, "");
  }

  public addAttachment(attachment) {
    const attachments = this.mailForm.controls.attachments.value;
    attachments.push(attachment);
    this.mailForm.patchValue({ attachments: attachments });
  }

  public removeAttachment(attachment: string) {
    const attachments: string[] =
      this.mailForm.controls.attachments.value ?? [];
    this.mailForm.patchValue({
      attachments: attachments.filter((item) => item !== attachment),
    });
  }

  public updateSubject(subject) {
    this.mailForm.patchValue({ subject: subject });
  }

  public updateMessage(message) {
    this.mailForm.patchValue({ message: message });
  }

  private clearMailSubject(): void {
    // use the template
    let subject = `[[${placeholder.myCompanyName}]] `;
    subject += `quote [[${placeholder.quoteNumber}]] `;
    subject += `for [[${placeholder.clientName}]] `;
    subject += `[[${placeholder.validityDates}]]`;
    let message = `[[${placeholder.clientGreetings}]]<br/><br/>`;
    message += `[[${placeholder.quoteSummary}]]<br/><br/>`;
    message += `[[${placeholder.mySignature}]]`;

    this.selectedMessageId = null;
    this.selectedReceivedAt = null;
    this.isMailSubjectSelected = false;
    this.replySourceChanged = true;
    this.mailForm.patchValue({
      to: [`[[${placeholder.clientEmails}]]`],
      cc: [],
      bcc: [],
      subject: subject,
      message: message,
      attachments: [],
    });
    this.subjectWysiwyg().updateValue(subject, true);
    this.messageWysiwyg().updateValue(message, true);
  }

  private changeMailSubject(email: QuoteEmail): void {
    this.isMailSubjectSelected = true;
    this.selectedMessageId = email.getMessageId();
    this.selectedReceivedAt = email.getReceivedAt();
    this.replySourceChanged = true;

    this.mailForm.patchValue({
      to: email.getTo() ? email.getTo().split(",") : [],
      cc: email.getCc() ? email.getCc().split(",") : [],
      bcc: [],
      subject: email.getSubject(),
      message: email.getMessage(),
      attachments: [],
    });
    this.subjectWysiwyg().updateValue(email.getSubject(), true);
    this.messageWysiwyg().updateValue(email.getMessage(), true);

    // The message carries only the [[previous_email_body]] placeholder. Changing the subject/message
    // above triggers the email PATCH (via valueChanges), which now carries the selected messageId; the
    // backend resolves and persists the raw quoted body keyed by messageId so it never passes through the
    // WYSIWYG editor, which would mangle its encoding. It is expanded back on send.
  }

  private createForm() {
    this.mailForm = this.formBuilder.group({
      to: [
        this.quote().getEmail().getTo()
          ? this.quote().getEmail().getTo().split(",")
          : [],
        [validateEmailArrayFormControl, Validators.required],
      ],
      cc: [
        this.quote().getEmail().getCc()
          ? this.quote().getEmail().getCc().split(",")
          : [],
        [validateEmailArrayFormControl],
      ],
      bcc: [
        this.quote().getEmail().getBcc()
          ? this.quote().getEmail().getBcc().split(",")
          : [],
        [validateEmailArrayFormControl],
      ],
      subject: [this.quote().getEmail().getSubject(), Validators.required],
      message: [this.quote().getEmail().getMessage(), Validators.required],
      attachments: [this.quote().getEmail().getAttachments()],
    });
  }

  public getSubjectValue(): string {
    return this.htmlSpecialCharsDecode(
      this.mailForm.controls.subject.value ?? "",
    ).replace(/<\/?[^>]+(>|$)/g, "");
  }

  private updateQuoteEmail() {
    this.quote().setEmail(
      new QuoteEmail(
        this.mailForm.controls.to.value.join(","),
        this.mailForm.controls.cc.value.join(","),
        this.mailForm.controls.bcc.value.join(","),
        this.getSubjectValue(),
        this.mailForm.controls.message.value,
        this.mailForm.controls.attachments.value,
        this.selectedMessageId,
        this.selectedReceivedAt,
      ),
    );

    this.emailChange.emit(this.quote().getEmail());
  }

  private htmlSpecialCharsDecode(text: string): string {
    const map: Record<string, string> = {
      "&amp;": "&",
      "&#038;": "&",
      "&nbsp;": " ",
      "&lt;": "<",
      "&gt;": ">",
      "&quot;": '"',
      "&#039;": "'",
      "&#8217;": "’",
      "&#8216;": "‘",
      "&#8211;": "–",
      "&#8212;": "—",
      "&#8230;": "…",
      "&#8221;": "”",
    };

    return text.replace(/&[\w\d#]{2,5};/g, (m) => map[m] ?? m);
  }

  private observeFormChanges() {
    this.subscription.add(
      this.mailForm.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((change) => {
          if (this.quote().getStatus() === quoteStatus.draft) {
            if (this.mailForm.valid) {
              this.updateQuoteEmail();
            } else {
              this.notifyService.toast(
                TOAST.WARN,
                "Correct all underlined fields",
              );
            }
          }

          this.emitValidity();
        }),
    );
  }

  private fetchMailSubjects() {
    this.mailboxLoading = true;
    this.subscription.add(
      this.quoteRepository.getMailMessages(this.quote().getId()).subscribe({
        next: (subjects) => {
          this.mailSubjects = [];
          this.isMailSubjectSelected = false;
          this.mailboxLoading = false;
          subjects.map((item) => {
            this.mailSubjects.push({ label: item.getSubject(), value: item });
            if (this.mailForm.controls.subject.value === item.getSubject()) {
              this.isMailSubjectSelected = true;
              this.selectedMessageId = item.getMessageId();
              this.selectedReceivedAt = item.getReceivedAt();
              // Restore messageId/receivedAt on the existing email object directly,
              // without emitting emailChange (which would trigger a backend PATCH).
              this.quote().getEmail()?.setMessageId(item.getMessageId());
              this.quote().getEmail()?.setReceivedAt(item.getReceivedAt());
            }
          });
        },
        error: (err) => {
          this.mailboxLoading = false;
          if (err?.error?.code === 1002) {
            this.notifyService.toast(
              TOAST.WARN,
              "Mailbox Temporarily Unavailable",
              "Could not fetch email subjects. Please try again.",
            );
          } else {
            this.notifyService.toast(
              TOAST.ERROR,
              "Mailbox Error",
              "Email subjects has not been fetched",
            );
          }
        },
      }),
    );
  }

  private fetchContactPersons(): void {
    if (this.quote().getClients().length) {
      this.subscription.add(
        this.clientContactPersonRepository
          .list(this.quote().getClients()[0])
          .subscribe({
            next: (list) => {
              list.map((person) => this.contactPersons.push(person.getEmail()));
            },
            error: (err) => {
              this.notifyService.toast(
                TOAST.ERROR,
                "Can't load account contact persons",
              );
            },
          }),
      );
    }
  }
}
